import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, Plus, Mail, Pencil, Trash2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const BUREAUS = ["CRED", "OneScore", "Experian", "CIBIL", "Paisabazaar", "Bajaj FinServ"];
const BUREAU_COLORS = {
    CRED: "#0f766e",
    OneScore: "#7c3aed",
    Experian: "#0369a1",
    CIBIL: "#dc2626",
    Paisabazaar: "#d97706",
    "Bajaj FinServ": "#4d7c0f",
};

function scoreBand(score) {
    if (!score) return { label: "—", tone: "stone" };
    if (score >= 800) return { label: "Excellent", tone: "emerald" };
    if (score >= 750) return { label: "Very Good", tone: "sky" };
    if (score >= 700) return { label: "Good", tone: "amber" };
    if (score >= 650) return { label: "Fair", tone: "orange" };
    return { label: "Needs Work", tone: "red" };
}

export default function CreditScore() {
    const { hasRole } = useAuth();
    const [trend, setTrend] = useState({ series: {}, latest: {} });
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({ bureau: "CRED", score: "", date: new Date().toISOString().slice(0, 10), notes: "" });
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const [t, r] = await Promise.all([
                api.get("/credit-scores/trend"),
                api.get("/credit-scores?limit=200"),
            ]);
            setTrend(t.data || { series: {}, latest: {} });
            setRows(r.data?.rows || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f, score: Number(f.score) };
            if (editingId) {
                await api.patch(`/credit-scores/${editingId}`, payload);
                toast.success("Score updated");
            } else {
                await api.post("/credit-scores", payload);
                toast.success("Score saved");
            }
            setOpen(false);
            setEditingId(null);
            setF({ ...f, score: "", notes: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF({ bureau: "CRED", score: "", date: new Date().toISOString().slice(0, 10), notes: "" }); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            bureau: row.bureau || "CRED",
            score: row.score ?? "",
            date: (row.date || "").slice(0, 10),
            notes: row.notes || "",
        });
        setOpen(true);
    };

    const syncGmail = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/credit-scores/sync", { days: 90 });
            toast.success(`${data.added} score${data.added === 1 ? "" : "s"} added (${data.scanned} scanned)`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this score entry?")) return;
        try { await api.delete(`/credit-scores/${id}`); toast.success("Removed"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    // Build chart data by merging all series into a single sorted-by-date array
    const chartData = (() => {
        const rowByDate = {};
        Object.entries(trend.series || {}).forEach(([bureau, pts]) => {
            pts.forEach(({ date, score }) => {
                rowByDate[date] = rowByDate[date] || { date };
                rowByDate[date][bureau] = score;
            });
        });
        return Object.values(rowByDate).sort((a, b) => a.date.localeCompare(b.date));
    })();

    const canSync = hasRole && hasRole("admin", "finance");

    return (
        <div data-testid="credit-score-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><TrendingUp size={12} /> Credit Health</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Your score across the bureaus.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Manually log a score from any bureau or sync directly from CRED / OneScore / Experian /
                        Paisabazaar emails.
                    </p>
                </div>
                <div className="flex gap-2">
                    {canSync && (
                        <Button variant="outline" onClick={syncGmail} disabled={busy} data-testid="cs-sync-gmail">
                            <Mail size={14} className="mr-1.5" /> Sync from Gmail
                        </Button>
                    )}
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="cs-add" onClick={openAdd}>
                                <Plus size={14} className="mr-1.5" /> Log score
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="cs-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">{editingId ? "Edit score entry" : "New score entry"}</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    Add a score from any bureau. Range 300-900.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="eyebrow">Bureau</Label>
                                        <Select value={f.bureau} onValueChange={set("bureau")}>
                                            <SelectTrigger className="mt-1.5" data-testid="cs-bureau"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {BUREAUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Score</Label>
                                        <Input type="number" min={300} max={900} required value={f.score} onChange={setInput("score")} className="mt-1.5 font-mono-figure" data-testid="cs-score" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Date</Label>
                                        <Input type="date" required value={f.date} onChange={setInput("date")} className="mt-1.5" data-testid="cs-date" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Notes (optional)</Label>
                                        <Input value={f.notes} onChange={setInput("notes")} className="mt-1.5" placeholder="e.g. after paying off HDFC card" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="cs-submit">
                                    {busy ? "Saving…" : editingId ? "Save changes" : "Save score"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Latest scores tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {Object.entries(trend.latest || {}).length === 0 && (
                    <div className="col-span-full rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-stone-500 text-sm" data-testid="cs-empty">
                        No scores yet. Log one manually or sync from Gmail to get started.
                    </div>
                )}
                {Object.entries(trend.latest || {}).map(([bureau, { date, score }]) => {
                    const band = scoreBand(score);
                    const color = BUREAU_COLORS[bureau] || "#57534e";
                    return (
                        <div key={bureau} className="rounded-lg border border-stone-200 bg-white p-4" data-testid={`cs-tile-${bureau}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-stone-500">{bureau}</div>
                                    <div className="font-mono-figure text-3xl mt-1" style={{ color }}>{score}</div>
                                </div>
                                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-${band.tone}-50 text-${band.tone}-700 border-${band.tone}-200`}>
                                    {band.label}
                                </span>
                            </div>
                            <div className="text-[11px] text-stone-500 mt-2">as of {fmtDate(date)}</div>
                        </div>
                    );
                })}
            </div>

            {/* Trend chart */}
            {chartData.length > 0 && (
                <div className="rounded-lg border border-stone-200 bg-white p-4 mb-6" data-testid="cs-chart">
                    <div className="eyebrow mb-3">Trend</div>
                    <div style={{ width: "100%", height: 300 }}>
                        <ResponsiveContainer>
                            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d?.slice(5)} />
                                <YAxis domain={[300, 900]} fontSize={11} />
                                <Tooltip />
                                <Legend />
                                {Object.keys(trend.series || {}).map((bureau) => (
                                    <Line
                                        key={bureau}
                                        type="monotone"
                                        dataKey={bureau}
                                        stroke={BUREAU_COLORS[bureau] || "#57534e"}
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* History table */}
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Bureau</th>
                                <th className="text-right">Score</th>
                                <th>Band</th>
                                <th>Source</th>
                                <th>Notes</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const band = scoreBand(r.score);
                                return (
                                    <tr key={r.id} data-testid={`cs-row-${r.id}`}>
                                        <td className="tabular-nums">{fmtDate(r.date)}</td>
                                        <td>{r.bureau}</td>
                                        <td className="text-right font-mono-figure">{r.score}</td>
                                        <td><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-${band.tone}-50 text-${band.tone}-700 border-${band.tone}-200`}>{band.label}</span></td>
                                        <td className="text-stone-500 text-[12px]">{r.source}</td>
                                        <td className="text-stone-500 text-[12px] truncate max-w-[280px]">{r.notes || "—"}</td>
                                        <td>
                                            <div className="inline-flex items-center gap-1">
                                                <button className="text-stone-400 hover:text-stone-900 p-1" onClick={() => openEdit(r)} aria-label="Edit" data-testid={`cs-edit-${r.id}`}>
                                                    <Pencil size={12} />
                                                </button>
                                                <button className="text-stone-400 hover:text-red-600 p-1" onClick={() => remove(r.id)} aria-label="Remove" data-testid={`cs-delete-${r.id}`}>
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-stone-500 py-14">No score history yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
